import type { PlainOpeningHoursDto, PlainSectionDto } from '@ordr-as/menu-builder-types/epos';

const DAY_NAMES = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'] as const;

/**
 * Check if a section is currently open based on its opening hours.
 * Returns true if:
 * - The section has no opening hours restrictions (hasOpeningHours === false)
 * - The current time (in the location's timezone) falls within the section's opening hours
 *
 * @param section - The section to check
 * @param zoneId - The location's timezone (e.g., "Europe/Oslo")
 */
export function isSectionOpen(section: PlainSectionDto, zoneId: string): boolean {
  if (!section.hasOpeningHours) {
    return true;
  }
  return isWithinOpeningHours(section.openingHours, new Date(), zoneId);
}

/**
 * Check if the given time falls within any of the opening hours.
 * Handles day rollover (e.g., restaurant open until 2 AM).
 *
 * @param hours - Opening hours configuration
 * @param currentTime - Current time (Date object)
 * @param zoneId - The location's timezone (e.g., "Europe/Oslo")
 */
export function isWithinOpeningHours(hours: PlainOpeningHoursDto[], currentTime: Date, zoneId: string): boolean {
  if (hours.length === 0) {
    return true;
  }

  const { dayName: currentDayName, timeMinutes: currentTimeMinutes } = getLocalTimeInfo(currentTime, zoneId);
  const previousDayName = getPreviousDayName(currentDayName);

  // Check today's hours
  const todayHours = hours.find((h) => h.day === currentDayName);
  if (
    todayHours &&
    !todayHours.closed &&
    isTimeWithinRange(currentTimeMinutes, todayHours.timeRange.startTime, todayHours.timeRange.endTime, false)
  ) {
    return true;
  }

  // Check if we're in the rollover period from previous day's hours
  const previousDayHours = hours.find((h) => h.day === previousDayName);
  if (
    previousDayHours &&
    !previousDayHours.closed &&
    previousDayHours.timeRange.endsOnNextDay &&
    isTimeWithinRange(currentTimeMinutes, '00:00', previousDayHours.timeRange.endTime, true)
  ) {
    return true;
  }

  return false;
}

/**
 * Get local time information for a given timezone.
 * Uses Intl.DateTimeFormat to convert the time to the specified timezone.
 */
function getLocalTimeInfo(date: Date, zoneId: string): { dayName: string; timeMinutes: number } {
  // Get the day of week in the specified timezone
  const dayFormatter = new Intl.DateTimeFormat('en-US', { timeZone: zoneId, weekday: 'long' });
  const dayName = dayFormatter.format(date).toUpperCase();

  // Get hours and minutes in the specified timezone
  const timeFormatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    hour12: false,
    minute: 'numeric',
    timeZone: zoneId,
  });
  const timeParts = timeFormatter.formatToParts(date);
  const hour = Number.parseInt(timeParts.find((p) => p.type === 'hour')?.value ?? '0', 10);
  const minute = Number.parseInt(timeParts.find((p) => p.type === 'minute')?.value ?? '0', 10);

  return {
    dayName,
    timeMinutes: hour * 60 + minute,
  };
}

/**
 * Get the previous day name.
 */
function getPreviousDayName(dayName: string): string {
  const index = DAY_NAMES.indexOf(dayName as typeof DAY_NAMES[number]);
  if (index === -1) {
    return 'SATURDAY';
  } // Fallback
  const previousIndex = (index + 6) % 7;
  return DAY_NAMES[previousIndex]!;
}

function getDayNameAtOffset(dayName: string, offset: number): string {
  const index = DAY_NAMES.indexOf(dayName as typeof DAY_NAMES[number]);
  if (index === -1) {
    return DAY_NAMES[0]!;
  }
  return DAY_NAMES[(index + offset) % 7]!;
}

function minutesToHHMM(minutes: number): string {
  const hh = Math.floor(minutes / 60)
    .toString()
    .padStart(2, '0');
  const mm = (minutes % 60).toString().padStart(2, '0');
  return `${hh}:${mm}`;
}

export interface OpeningHoursLike {
  day: string;
  closed?: boolean;
  timeRange?: { startTime: string; endTime: string; endsOnNextDay?: boolean };
}

export type SectionNextOpen =
  | { kind: 'opensToday'; time: string }
  | { kind: 'opensTomorrow'; time: string }
  | { kind: 'opensWeekday'; day: string; time: string }
  | { kind: 'closedIndefinitely' };

/**
 * Find when the section will next open. Assumes the section is currently closed.
 * For offsets >= 1 (tomorrow and later), the location's opening hours are
 * intersected with the section's hours so the displayed start time reflects
 * when the location itself opens.
 * Returns `closedIndefinitely` when no upcoming open day is found within a week.
 */
export function findSectionNextOpen(
  sectionHours: OpeningHoursLike[],
  locationHours: OpeningHoursLike[],
  currentTime: Date,
  zoneId: string
): SectionNextOpen {
  if (sectionHours.length === 0) {
    return { kind: 'closedIndefinitely' };
  }

  const { dayName: currentDayName, timeMinutes: currentTimeMinutes } = getLocalTimeInfo(currentTime, zoneId);

  for (let offset = 0; offset < 7; offset++) {
    const dayName = getDayNameAtOffset(currentDayName, offset);
    const sectionDay = sectionHours.find((h) => h.day === dayName);
    if (!sectionDay || sectionDay.closed || !sectionDay.timeRange) {
      continue;
    }

    let startMinutes = parseTimeToMinutes(sectionDay.timeRange.startTime);

    if (offset >= 1 && locationHours.length > 0) {
      const locationDay = locationHours.find((h) => h.day === dayName);
      if (locationDay) {
        if (locationDay.closed || !locationDay.timeRange) {
          continue;
        }
        const locationStart = parseTimeToMinutes(locationDay.timeRange.startTime);
        startMinutes = Math.max(startMinutes, locationStart);
      }
    }

    if (offset === 0 && startMinutes <= currentTimeMinutes) {
      continue;
    }

    const time = minutesToHHMM(startMinutes);
    if (offset === 0) {
      return { kind: 'opensToday', time };
    }
    if (offset === 1) {
      return { kind: 'opensTomorrow', time };
    }
    return { day: dayName, kind: 'opensWeekday', time };
  }

  return { kind: 'closedIndefinitely' };
}

/**
 * Parse time string (HH:MM) to minutes since midnight.
 */
function parseTimeToMinutes(time: string): number {
  const parts = time.split(':').map(Number);
  const hours = parts[0] ?? 0;
  const minutes = parts[1] ?? 0;
  return hours * 60 + minutes;
}

/**
 * Check if the current time in minutes falls within a time range.
 * @param currentMinutes - Current time in minutes since midnight
 * @param startTime - Start time as HH:MM string
 * @param endTime - End time as HH:MM string
 * @param isRolloverCheck - If true, only checks from midnight to end time
 */
function isTimeWithinRange(
  currentMinutes: number,
  startTime: string,
  endTime: string,
  isRolloverCheck: boolean
): boolean {
  const startMinutes = parseTimeToMinutes(startTime);
  const endMinutes = parseTimeToMinutes(endTime);

  if (isRolloverCheck) {
    // For rollover, we're checking the next day portion (midnight to end time)
    return currentMinutes < endMinutes;
  }

  // Special case: 00:00 to 00:00 means "open all day"
  // Backend treats this as 00:00 to 23:59:59.999 due to minusNanos(1) wrapping
  if (startMinutes === 0 && endMinutes === 0) {
    return true;
  }

  // Normal range check (start <= current < end)
  // If end is less than start, it means it rolls over to next day
  if (endMinutes < startMinutes) {
    // Hours like 18:00 - 02:00: we're open if current >= start (on same day portion)
    return currentMinutes >= startMinutes;
  }

  return currentMinutes >= startMinutes && currentMinutes < endMinutes;
}
